import { Dispatch } from "react";
import { toast } from "react-toastify";
import { getPayerDetailsBuilder, updatePrimaryAccount, updateSecondaryAccount } from "./actions/InvoiceActions";
import { AccountType } from "./interfaces/Accounts";
import { CACHE_SESSION_CARDS, CACHE_SESSION_CHECKS } from "./reducers/PaymentReducer";
import { getAndClearSessionStorage } from "./utils";

export const THREEDS_FLOW_CONTEXT_KEY = "THREEDS_FLOW_CONTEXT_KEY";
const THREEDS_FLOW_CONTEXT_MAX_AGE_MS = 15 * 60 * 1000;

export type ThreeDSFlowType = "add-card" | "invoice-payment" | "deposit-payment";

export interface ThreeDSFlowContext {
    flowType: ThreeDSFlowType;
    primaryAccountId: string;
    secondaryAccountId: string | null;
    selectedPaymentToken?: string | null;
    saveOnFile?: boolean | null;
    makeDefault?: boolean | null;
    createdAt: number;
    returnPath?: string | null;
}

export function cacheThreeDSFlowContext(context: Omit<ThreeDSFlowContext, "createdAt">) {
    const value: ThreeDSFlowContext = {
        ...context,
        createdAt: Date.now(),
    };
    sessionStorage.setItem(THREEDS_FLOW_CONTEXT_KEY, JSON.stringify(value));
    return value;
}

export function consumeThreeDSFlowContext() {
    return getAndClearSessionStorage(THREEDS_FLOW_CONTEXT_KEY) as ThreeDSFlowContext | null;
}

export function clearThreeDSFlowContext() {
    try {
        sessionStorage.removeItem(THREEDS_FLOW_CONTEXT_KEY);
    } catch (e) {
    }
}

export function validateThreeDSFlowContext(
    context: ThreeDSFlowContext | null,
    flowType: ThreeDSFlowType
): context is ThreeDSFlowContext {
    if (!context || context.flowType !== flowType) {
        return false;
    }
    if (!context.primaryAccountId) {
        return false;
    }
    return Date.now() - context.createdAt <= THREEDS_FLOW_CONTEXT_MAX_AGE_MS;
}

export async function rehydrateThreeDSAccountContext(
    dispatch: Dispatch<any>,
    context: ThreeDSFlowContext,
    primaryAccounts: any,
    secondaryAccounts: any,
    currentUser: any,
    accountType: AccountType,
): Promise<{ primaryAccount: any, secondaryAccount: any, payerDetails: any }> {
    const resolvedPrimaryAccount: any = primaryAccounts?.[context.primaryAccountId] ?? null;
    if (!resolvedPrimaryAccount) {
        throw new Error("The original primary account is no longer available.");
    }

    if (context.primaryAccountId !== resolvedPrimaryAccount.PrimaryAcct) {
        throw new Error("Unable to restore the original primary account.");
    }

    if (resolvedPrimaryAccount.PrimaryAcct !== context.primaryAccountId) {
        throw new Error("Unable to restore the original primary account.");
    }

    dispatch(updatePrimaryAccount(context.primaryAccountId, false));

    let resolvedSecondaryAccount: any = null;
    if (accountType === AccountType.SoldTo && context.secondaryAccountId) {
        resolvedSecondaryAccount = secondaryAccounts?.[context.secondaryAccountId]
            ?? resolvedPrimaryAccount?.DropDownItems?.find(account => account.PrimaryAcct === context.secondaryAccountId)
            ?? null;

        if (!resolvedSecondaryAccount) {
            throw new Error("The original sold-to account is no longer available.");
        }
        dispatch(updateSecondaryAccount(context.secondaryAccountId));
    }

    const payerDetails = await dispatch(getPayerDetailsBuilder(
        resolvedPrimaryAccount,
        resolvedSecondaryAccount,
        currentUser.primary_account_type_id,
    ));

    return {
        primaryAccount: resolvedPrimaryAccount,
        secondaryAccount: resolvedSecondaryAccount,
        payerDetails,
    };
}

export function getSessionPaymentMethodsForPayer(payerAccountId: string) {
    const sessionCards = JSON.parse(sessionStorage.getItem(CACHE_SESSION_CARDS) ?? "{}");
    const sessionChecks = JSON.parse(sessionStorage.getItem(CACHE_SESSION_CHECKS) ?? "{}");
    const payerCards = Object.values(sessionCards?.[payerAccountId] ?? {});
    const payerChecks = Object.values(sessionChecks?.[payerAccountId] ?? {});
    return [...payerCards, ...payerChecks];
}

export function resolveRestoredPaymentOption(context: ThreeDSFlowContext, payerDetails: any, payerAccountId: string) {
    const persistedPaymentMethods = payerDetails?.payment_cards ?? [];
    const sessionPaymentMethods = getSessionPaymentMethodsForPayer(payerAccountId);
    const allPaymentMethods = [...persistedPaymentMethods, ...sessionPaymentMethods];

    if (!context.selectedPaymentToken) {
        throw new Error("We couldn't determine which payment method was used for 3DS.");
    }

    const paymentOption = allPaymentMethods.find((paymentMethod: any) => paymentMethod.payment_card_token === context.selectedPaymentToken);
    if (!paymentOption) {
        throw new Error("The original payment method is no longer available for this account.");
    }

    return paymentOption;
}

export function handleThreeDSContextError(history, fallbackPath: string, message: string) {
    clearThreeDSFlowContext();
    toast.error(message, { position: toast.POSITION.TOP_RIGHT });
    history.replace(fallbackPath);
}
